import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { InfoPageLayout } from "@/components/pages/InfoPageLayout";
import { DISCLAIMER_SKB, SUPPORTED_YEARS } from "@/data/holidays";
import { webPageSchema, breadcrumbSchema, organizationSchema, ldScripts } from "@/lib/seo/schema";

const TITLE = "About Indonesian Calendar - Data Sources & Methods";
const DESC = "About Indonesian Calendar: SKB holiday data sources, Hijri/Javanese/Balinese conversion methods, and disclaimer.";

export const Route = createFileRoute("/en/about")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "About Indonesian Calendar" },
      { property: "og:description", content: "Sources, conversion methods, and disclaimer." },
    ],
    links: [{ rel: "canonical", href: "https://hari.co.id/en/about/" }],
    scripts: ldScripts([
      webPageSchema({ url: "/en/about", name: TITLE, description: DESC, type: "AboutPage", locale: "en" }),
      breadcrumbSchema([
        { name: "Home", url: "/en" },
        { name: "About", url: "/en/about" },
      ]),
      organizationSchema("en"),
    ]),
  }),
  component: AboutPageEn,
});

function AboutPageEn() {
  const yearStart = SUPPORTED_YEARS[0];
  const yearEnd = SUPPORTED_YEARS[SUPPORTED_YEARS.length - 1];
  return (
    <SiteShell locale="en">
      <InfoPageLayout
        variant="about"
        title="About Indonesian Calendar"
        subtitle="A complete date reference: Gregorian, Hijri, Javanese & Balinese, with public holidays, joint leave, and tools."
        badge="i"
        locale="en"
        breadcrumbs={[{ label: "Home", href: "/en" }, { label: "About" }]}
        aside={
          <div className="space-y-3">
            <div className="rounded-xl border border-border bg-card p-4 text-sm">
              <h3 className="mb-2 font-semibold">At a glance</h3>
              <ul className="space-y-1 text-muted-foreground">
                <li><strong className="text-foreground">{yearEnd - yearStart + 1}</strong> years supported ({yearStart}–{yearEnd})</li>
                <li><strong className="text-foreground">4</strong> calendar systems</li>
                <li><strong className="text-foreground">PDF</strong> export per month</li>
              </ul>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 text-sm">
              <h3 className="mb-2 font-semibold">More pages</h3>
              <ul className="space-y-1.5 text-muted-foreground">
                <li><Link className="hover:text-primary" to={"/en/contact" as never}>Contact</Link></li>
                <li><Link className="hover:text-primary" to={"/en/privacy-policy" as never}>Privacy Policy</Link></li>
                <li><Link className="hover:text-primary" to={"/en/terms" as never}>Terms & Conditions</Link></li>
              </ul>
            </div>
          </div>
        }
      >
        <p>
          <strong>Indonesian Calendar</strong> is a complete date reference
          covering Gregorian, Hijri, Javanese (pasaran &amp; weton) and
          Balinese (pawukon) calendars, plus public holidays, joint leave
          (cuti bersama), and notable national/international days.
        </p>

        <h2 id="source">Holiday Data Source</h2>
        <p>
          Public holidays and joint leave data come from the{" "}
          <strong>SKB 3 Menteri</strong> (Joint Ministerial Decree) for{" "}
          {SUPPORTED_YEARS[0]}–{SUPPORTED_YEARS[SUPPORTED_YEARS.length - 1]}.
          Years without an official decree are marked as{" "}
          <em>estimated data</em> and updated when published.
        </p>
        <p className="rounded-md bg-muted p-4 text-sm">{DISCLAIMER_SKB}</p>

        <h2 id="methods">Calendar Conversion Methods</h2>
        <div className="not-prose mt-3 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="font-semibold">Hijri</h3>
            <p className="mt-1 text-sm text-muted-foreground">Civil arithmetic (Kuwaiti). May differ by 1 day from sighting.</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="font-semibold">Javanese</h3>
            <p className="mt-1 text-sm text-muted-foreground">Sultan Agungan, epoch 1 Sura 1555 J (8 July 1633), Friday Legi.</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="font-semibold">Balinese</h3>
            <p className="mt-1 text-sm text-muted-foreground">Pawukon, 210-day cycle: saptawara, pancawara, triwara, wuku.</p>
          </div>
        </div>

        <h2 id="disclaimer">Disclaimer</h2>
        <p>
          Data on this site is for <strong>reference</strong> and may contain
          errors. For official needs (government, religious schedules, legal
          documents) please verify with official sources.
        </p>

        <h2 id="navigation">Navigation</h2>
        <ul>
          <li><Link to={"/en" as never}>Home</Link></li>
          <li><Link to={"/tools/kalkulator-hari" as never}>Day Calculator</Link></li>
          <li><Link to={"/tools/cek-weton" as never}>Weton Check</Link></li>
        </ul>
      </InfoPageLayout>
    </SiteShell>
  );
}